"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.default = exports.DmsSystemMonitor = void 0;
var _react = _interopRequireWildcard(require("react"));
var _material = require("@mui/material");
var _iconsMaterial = require("@mui/icons-material");
var _DmsAltBox = require("../../components/DmsAltBox/DmsAltBox");
var _DmsFetch = require("../../components/DmsFetch/DmsFetch");
var _DmsPage = require("../../components/DmsPage/DmsPage");
var _PieChart = require("@mui/x-charts/PieChart");
var _LineChart = require("@mui/x-charts/LineChart");
var _NamedRoutes = require("../../constants/NamedRoutes");
var _index = require("../../index");
function _interopRequireWildcard(e, t) { if ("function" == typeof WeakMap) var r = new WeakMap(), n = new WeakMap(); return (_interopRequireWildcard = function (e, t) { if (!t && e && e.__esModule) return e; var o, i, f = { __proto__: null, default: e }; if (null === e || "object" != typeof e && "function" != typeof e) return f; if (o = t ? n : r) { if (o.has(e)) return o.get(e); o.set(e, f); } for (const t in e) "default" !== t && {}.hasOwnProperty.call(e, t) && ((i = (o = Object.defineProperty) && Object.getOwnPropertyDescriptor(e, t)) && (i.get || i.set) ? o(f, t, i) : f[t] = e[t]); return f; })(e, t); }
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
const PROMETHEUS_URL = 'http://localhost:9090/api/v1';
const ONE_HOUR_IN_SECONDS = 3600;
const REFRESH_INTERVAL = 5000;
const bytes_to_gigabytes_conversion = 1024 ** 3;
const milliseconds_to_seconds_conversion = 1000;
const step = 30;

/**
 * DmsSystemMonitor component renders disk, cpu, ram, and OS information. 
 * 
 * 
 * It fetchs data from dmspy_runtime scripts, so for correct usage
 * its *** MANDATORY *** to have Prometheus client installed and running. 
 * Follow WIKI: <https://pollux-wiki.accenture.com/pt-br/software/digital/base-v5/monitoring> 
 * 
 * @component
 * @example
 * return (
 *   <DmsSystemMonitor />
 * )
 * 
 *
 */

const DmsSystemMonitor = () => {
  const [systemData, setSystemData] = (0, _react.useState)(null);
  const [metrics, setMetrics] = (0, _react.useState)({
    ramMemTotal: null,
    ramMemUsed: null,
    cpuPercentage: null,
    diskFree: null,
    diskTotal: null,
    processorCount: null
  });
  (0, _react.useEffect)(() => {
    const fetchSystemData = async () => {
      try {
        var _res$data;
        const res = await (0, _DmsFetch.fetchData)(_NamedRoutes.ROUTES.SystemMonitor, "GET", true);
        setSystemData((res === null || res === void 0 || (_res$data = res.data) === null || _res$data === void 0 ? void 0 : _res$data.data) || null);
      } catch (error) {
        console.error("Error fetching system data:", error);
      }
    };
    fetchSystemData();
  }, []);
  (0, _react.useEffect)(() => {
    const fetchMetrics = async () => {
      try {
        const [memoryData, systemData] = await Promise.all([fetchMemoryMetrics(), fetchSystemMetrics()]);
        setMetrics(prev => _objectSpread(_objectSpread(_objectSpread({}, prev), memoryData), systemData));
      } catch (error) {
        console.error("Error fetching metrics:", error);
      }
    };
    const interval = setInterval(fetchMetrics, REFRESH_INTERVAL);
    fetchMetrics();
    return () => clearInterval(interval);
  }, []);
  const fetchMemoryMetrics = async () => {
    const end = Date.now() / milliseconds_to_seconds_conversion;
    const start = end - ONE_HOUR_IN_SECONDS;
    const queries = ['system_mem_used', 'system_mem_total', 'system_cpu_total'];
    const urls = queries.map(query => "".concat(PROMETHEUS_URL, "/query_range?query=").concat(query, "&start=").concat(start, "&end=").concat(end, "&step=").concat(step));
    const responses = await Promise.all(urls.map(url => fetch(url)));
    const data = await Promise.all(responses.map(res => res.json()));
    const processChartData = function (result) {
      let isCpu = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : false;
      if (result.status !== "success" || !result.data.result.length) return null;
      const values = result.data.result[0].values;
      return {
        labels: values.map(item => new Date(item[0] * milliseconds_to_seconds_conversion)),
        series: values.map(item => isCpu ? parseFloat(item[1]) : parseFloat(item[1]) / bytes_to_gigabytes_conversion)
      };
    };
    return {
      ramMemUsed: processChartData(data[0]),
      ramMemTotal: processChartData(data[1]),
      cpuPercentage: processChartData(data[2], true)
    };
  };
  const fetchSystemMetrics = async () => {
    try {
      var _data$, _data$2, _data$3, _data$4, _data$5, _data$6;
      const queries = ['system_cpu_count', 'system_disk_root_free', 'system_disk_root_total'];
      const responses = await Promise.all(queries.map(query => fetch("".concat(PROMETHEUS_URL, "/query?query=").concat(query))));
      const data = await Promise.all(responses.map(res => res.json()));

      // Process all metrics
      const result = {};
      if (((_data$ = data[0]) === null || _data$ === void 0 ? void 0 : _data$.status) === "success" && ((_data$2 = data[0]) === null || _data$2 === void 0 || (_data$2 = _data$2.data) === null || _data$2 === void 0 || (_data$2 = _data$2.result) === null || _data$2 === void 0 ? void 0 : _data$2.length) > 0) {
        result.processorCount = parseFloat(data[0].data.result[0].value[1]);
      }
      if (((_data$3 = data[1]) === null || _data$3 === void 0 ? void 0 : _data$3.status) === "success" && ((_data$4 = data[1]) === null || _data$4 === void 0 || (_data$4 = _data$4.data) === null || _data$4 === void 0 || (_data$4 = _data$4.result) === null || _data$4 === void 0 ? void 0 : _data$4.length) > 0) {
        result.diskFree = parseFloat(data[1].data.result[0].value[1]) / bytes_to_gigabytes_conversion;
      }
      if (((_data$5 = data[2]) === null || _data$5 === void 0 ? void 0 : _data$5.status) === "success" && ((_data$6 = data[2]) === null || _data$6 === void 0 || (_data$6 = _data$6.data) === null || _data$6 === void 0 || (_data$6 = _data$6.result) === null || _data$6 === void 0 ? void 0 : _data$6.length) > 0) {
        result.diskTotal = parseFloat(data[2].data.result[0].value[1]) / bytes_to_gigabytes_conversion;
      }
      return result;
    } catch (error) {
      console.error("Error fetching system metrics:", error);
      return {};
    }
  };
  const renderSystemInfo = () => {
    var _systemData$os, _systemData$os2, _systemData$os3, _systemData$os4;
    return /*#__PURE__*/_react.default.createElement(_material.List, {
      sx: {
        width: '100%',
        maxWidth: 360,
        bgcolor: 'background.paper'
      }
    }, [{
      icon: /*#__PURE__*/_react.default.createElement(_iconsMaterial.DesktopWindows, null),
      label: _index.intl.get("Platform").d("Platform"),
      value: systemData === null || systemData === void 0 || (_systemData$os = systemData.os) === null || _systemData$os === void 0 ? void 0 : _systemData$os.Platform
    }, {
      icon: /*#__PURE__*/_react.default.createElement(_iconsMaterial.Memory, null),
      label: _index.intl.get("Version").d("Version"),
      value: systemData === null || systemData === void 0 || (_systemData$os2 = systemData.os) === null || _systemData$os2 === void 0 ? void 0 : _systemData$os2.Version
    }, {
      icon: /*#__PURE__*/_react.default.createElement(_iconsMaterial.NewReleases, null),
      label: _index.intl.get("Release").d("Release"),
      value: systemData === null || systemData === void 0 || (_systemData$os3 = systemData.os) === null || _systemData$os3 === void 0 ? void 0 : _systemData$os3.Release
    }, {
      icon: /*#__PURE__*/_react.default.createElement(_iconsMaterial.Architecture, null),
      label: _index.intl.get("Architecture").d("Architecture"),
      value: systemData === null || systemData === void 0 || (_systemData$os4 = systemData.os) === null || _systemData$os4 === void 0 ? void 0 : _systemData$os4.Architecture
    }, {
      icon: /*#__PURE__*/_react.default.createElement(_iconsMaterial.AccountTree, null),
      label: _index.intl.get("Processors").d("Processors"),
      value: metrics.processorCount
    }].map((item, index) => /*#__PURE__*/_react.default.createElement(_material.ListItem, {
      key: index
    }, /*#__PURE__*/_react.default.createElement(_material.ListItemAvatar, null, /*#__PURE__*/_react.default.createElement(_material.Avatar, null, item.icon)), /*#__PURE__*/_react.default.createElement(_material.ListItemText, {
      primary: item.label,
      secondary: item.value || 'N/A'
    }))));
  };
  const renderMemoryCpuChart = () => {
    var _metrics$ramMemTotal$, _metrics$ramMemUsed$s, _metrics$cpuPercentag;
    if (!metrics.ramMemUsed || !metrics.ramMemTotal || !metrics.cpuPercentage) {
      return /*#__PURE__*/_react.default.createElement(_material.Typography, null, _index.intl.get('LoadingData').d('Loading data'), " ... ");
    }
    return /*#__PURE__*/_react.default.createElement(_LineChart.LineChart, {
      xAxis: [{
        data: metrics.ramMemUsed.labels,
        scaleType: 'time',
        valueFormatter: value => {
          const date = new Date(value);
          return "".concat(date.getHours(), ":").concat(String(date.getMinutes()).padStart(2, '0'));
        }
      }],
      yAxis: [{
        label: " ".concat(_index.intl.get('Memory').d('Memory'), " (GB)"),
        id: 'memoryAxis'
      }, {
        label: " ".concat(_index.intl.get('CPUUtilization').d('CPU Utilization'), " %"),
        id: 'cpuAxis',
        min: 0,
        max: 100
      }],
      leftAxis: "memoryAxis",
      rightAxis: "cpuAxis",
      series: [{
        data: metrics.ramMemTotal.series,
        area: false,
        label: "".concat(_index.intl.get('MemoryTotal').d('Memory Total'), " (GB) | \n                        ").concat(_index.intl.get('Last').d('Last'), ": ").concat(((_metrics$ramMemTotal$ = metrics.ramMemTotal.series.slice(-1)[0]) === null || _metrics$ramMemTotal$ === void 0 ? void 0 : _metrics$ramMemTotal$.toFixed(2)) || 'N/A'),
        showMark: false,
        color: 'blue',
        yAxisKey: 'memoryAxis'
      }, {
        data: metrics.ramMemUsed.series,
        area: true,
        label: "".concat(_index.intl.get('MemoryUsed').d('Memory Used'), " (GB) | \n                        ").concat(_index.intl.get('Last').d('Last'), ": ").concat(((_metrics$ramMemUsed$s = metrics.ramMemUsed.series.slice(-1)[0]) === null || _metrics$ramMemUsed$s === void 0 ? void 0 : _metrics$ramMemUsed$s.toFixed(2)) || 'N/A'),
        showMark: false,
        color: 'rgba(60, 141, 188, 1)',
        yAxisKey: 'memoryAxis'
      }, {
        data: metrics.cpuPercentage.series,
        label: "".concat(_index.intl.get('CPUUsage').d('CPU Usage'), " (%) | \n                        ").concat(_index.intl.get('Last').d('Last'), ": ").concat(((_metrics$cpuPercentag = metrics.cpuPercentage.series.slice(-1)[0]) === null || _metrics$cpuPercentag === void 0 ? void 0 : _metrics$cpuPercentag.toFixed(2)) || 'N/A'),
        showMark: false,
        color: 'rgba(255, 0, 0, 0.7)',
        yAxisKey: 'cpuAxis',
        curve: 'linear'
      }],
      grid: {
        vertical: true,
        horizontal: true
      },
      height: 500,
      margin: {
        top: 50,
        right: 50,
        bottom: 100,
        left: 70
      },
      slotProps: {
        legend: {
          direction: 'row',
          position: {
            vertical: 'bottom',
            horizontal: 'middle'
          },
          labelStyle: {
            fontSize: '0.8rem',
            whiteSpace: 'nowrap'
          }
        }
      }
    });
  };
  const renderDiskInfo = () => {
    if (!metrics.diskTotal || !metrics.diskFree) {
      return /*#__PURE__*/_react.default.createElement(_material.Typography, null, _index.intl.get('LoadingData').d('Loading data'), " ... ");
    }
    const diskUsed = metrics.diskTotal - metrics.diskFree;
    const diskUsagePercentage = (diskUsed / metrics.diskTotal * 100).toFixed(2);
    const series = [{
      innerRadius: 40,
      outerRadius: 80,
      id: 'out',
      data: [{
        id: 0,
        value: diskUsed,
        label: "".concat(_index.intl.get('Used').d('Used'), " ").concat(diskUsed.toFixed(2), " GB")
      }, {
        id: 1,
        value: metrics.diskFree,
        label: "".concat(_index.intl.get('Free').d('Free'), " ").concat(metrics.diskFree.toFixed(2), " GB")
      }]
    }, {
      innerRadius: 0,
      outerRadius: 30,
      id: 'inner',
      data: [{
        id: 2,
        value: metrics.diskTotal.toFixed(2),
        label: "".concat(_index.intl.get('Total').d('Total'), " ").concat(metrics.diskTotal.toFixed(2), " GB"),
        color: 'blue'
      }]
    }];
    return /*#__PURE__*/_react.default.createElement(_react.default.Fragment, null, /*#__PURE__*/_react.default.createElement(_PieChart.PieChart, {
      series: series,
      width: 400,
      height: 200
    }), /*#__PURE__*/_react.default.createElement(_material.Typography, {
      variant: "h5"
    }, " ", diskUsagePercentage, "% ", _index.intl.get('Used').d('Used'), " "));
  };
  return /*#__PURE__*/_react.default.createElement(_DmsPage.DmsPage, {
    name: "System Monitor"
  }, /*#__PURE__*/_react.default.createElement(_material.Box, {
    textAlign: "center",
    m: 2
  }, /*#__PURE__*/_react.default.createElement(_material.Grid, {
    container: true,
    spacing: 2
  }, /*#__PURE__*/_react.default.createElement(_material.Grid, {
    item: true,
    xs: 12,
    sm: 12,
    lg: 4
  }, /*#__PURE__*/_react.default.createElement(_DmsAltBox.DmsAltBox.Card, null, /*#__PURE__*/_react.default.createElement(_DmsAltBox.DmsAltBox.Header, null, /*#__PURE__*/_react.default.createElement(_material.Typography, {
    variant: "h5"
  }, _index.intl.get('SystemInformation').d('System Information')), /*#__PURE__*/_react.default.createElement(_DmsAltBox.DmsAltBox.HeaderRight, null, /*#__PURE__*/_react.default.createElement(_iconsMaterial.DesktopWindows, {
    fontSize: "large"
  }))), /*#__PURE__*/_react.default.createElement(_DmsAltBox.DmsAltBox.Body, null, renderSystemInfo())), /*#__PURE__*/_react.default.createElement(_DmsAltBox.DmsAltBox.Card, null, /*#__PURE__*/_react.default.createElement(_DmsAltBox.DmsAltBox.Header, null, /*#__PURE__*/_react.default.createElement(_material.Typography, {
    variant: "h5"
  }, _index.intl.get('DiskUsage').d('Disk Usage')), /*#__PURE__*/_react.default.createElement(_DmsAltBox.DmsAltBox.HeaderRight, null, /*#__PURE__*/_react.default.createElement(_iconsMaterial.SdStorage, {
    fontSize: "large"
  }))), /*#__PURE__*/_react.default.createElement(_DmsAltBox.DmsAltBox.Body, null, renderDiskInfo()))), /*#__PURE__*/_react.default.createElement(_material.Grid, {
    container: true,
    item: true,
    xs: 12,
    sm: 12,
    lg: 8
  }, /*#__PURE__*/_react.default.createElement(_DmsAltBox.DmsAltBox.Card, null, /*#__PURE__*/_react.default.createElement(_DmsAltBox.DmsAltBox.Header, null, /*#__PURE__*/_react.default.createElement(_material.Typography, {
    variant: "h5"
  }, _index.intl.get('RAMCPUUsage').d('RAM & CPU Usage')), /*#__PURE__*/_react.default.createElement(_DmsAltBox.DmsAltBox.HeaderRight, null, /*#__PURE__*/_react.default.createElement(_iconsMaterial.Memory, {
    fontSize: "large"
  }))), /*#__PURE__*/_react.default.createElement(_DmsAltBox.DmsAltBox.Body, null, renderMemoryCpuChart()))))));
};
exports.DmsSystemMonitor = DmsSystemMonitor;
var _default = exports.default = DmsSystemMonitor;